// AboutPage — App version, credits, legal, social links
// Layout: hero w/ logo, version, team credits, links

import { useNavigate } from 'react-router-dom';
import { PartnerLogo, MerchantLogo } from '../../components/Brand';

export function AboutPage() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">About</div>
        <div className="w-9" />
      </div>

      {/* Hero */}
      <div className="mx-4 mt-6 text-center">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-[24px] text-[48px] bg-tf-blue" style={{ background: 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)' }}>🚀</div>
        <h1 className="mt-4 text-[24px] font-bold tracking-tight text-tf-text-1">Funder Pro</h1>
        <p className="mt-1 text-[13px] text-tf-text-3">Smarter investing, powered by AI</p>
        <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white border border-tf-border px-3 py-1">
          <span className="h-1.5 w-1.5 rounded-full bg-tf-profit" />
          <span className="text-[11px] font-semibold text-tf-text-1">v3.2.1 · Build 20260724</span>
        </div>
      </div>

      {/* App info */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">App</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <Row label="Version"     value="3.2.1" />
        <Row label="Build"       value="20260724" />
        <Row label="Last updated" value="July 24, 2026" />
        <Row label="Size"        value="12.4 MB" />
        <Row label="Platform"    value="iOS 18.0+" last />
      </div>

      {/* Legal */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Legal</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <Link onClick={() => nav('/terms-conditions')}  label="Terms & conditions" />
        <Link onClick={() => nav('/privacy-notice')}   label="Privacy notice" />
        <Link onClick={() => nav('/risk-disclosure')}  label="Risk disclosure" />
        <Link onClick={() => alert('Open source licenses')} label="Open source licenses" last />
      </div>

      {/* Powered by */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Powered by</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        {[
          { name: 'stripe',       label: 'Stripe (payments)' },
          { name: 'persona',      label: 'Persona (KYC)' },
          { name: 'twelvedata',   label: 'TwelveData (prices)' },
          { name: 'rithmic',      label: 'Rithmic (data)' },
          { name: 'tradingview',  label: 'TradingView (charts)' },
          { name: 'twilio',       label: 'Twilio (SMS)' },
          { name: 'coinbase',     label: 'Coinbase (crypto)' },
        ].map((p, i, a) => (
          <div key={p.name} data-testid={`about-partner-${p.name}`} className={['flex items-center justify-between px-4 py-2.5', i < a.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
            <PartnerLogo name={p.name} height={20} />
            <span className="text-[12px] text-tf-text-3">{p.label}</span>
          </div>
        ))}
      </div>

      {/* Social */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Follow us</h2>
      <div className="mx-4 flex items-center justify-center gap-2.5">
        {['discord', 'telegram', 'x', 'instagram', 'youtube', 'tiktok'].map((n) => (
          <button key={n} onClick={() => alert('Open ' + n)} data-testid={`about-social-${n}`} className="flex h-12 w-12 items-center justify-center rounded-[14px] glass active:scale-95 transition">
            <MerchantLogo name={n} size={26} />
          </button>
        ))}
      </div>

      {/* Team */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Team</h2>
      <div className="mx-4 rounded-[16px] border border-tf-border bg-white p-4">
        <p className="text-[13px] text-tf-text-1 leading-relaxed">Built with care by a team of 12 traders, engineers, and designers across San Francisco, New York, and London. Backed by Founders Fund and Andreessen Horowitz.</p>
      </div>

      <p className="mt-4 px-4 text-center text-[10px] text-tf-text-3">© 2026 Funder Pro Inc. All rights reserved.<br />Made with ☕ in SF</p>
    </div>
  );
}

function Row({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div className={['flex items-center justify-between px-4 py-2.5', last ? '' : 'border-b border-tf-border'].join(' ')}>
      <span className="text-[12px] text-tf-text-3">{label}</span>
      <span className="text-[13px] font-medium text-tf-text-1 tabular-nums">{value}</span>
    </div>
  );
}

function Link({ onClick, label, last }: { onClick: () => void; label: string; last?: boolean }) {
  return (
    <button onClick={onClick} className={['flex w-full items-center justify-between px-4 py-2.5 active:bg-tf-surface-1 transition', last ? '' : 'border-b border-tf-border'].join(' ')} data-testid={`about-link-${label}`}>
      <span className="text-[14px] text-tf-text-1">{label}</span>
      <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
    </button>
  );
}
